<template>
  <div class="cms-help-center">
    <cms-page-header
      :title="$t('cms.faq.title')"
      :create-text="$t('cms.faq.createCategory')"
      @create="navigateToCreateCategory"
    />

    <cms-filter-bar>
      <cms-filter-group :label="$t('cms.faq.filters.language')">
        <select v-model="selectedLanguage" class="cms-select">
          <option v-for="language in languageOptions" :key="language.value" :value="language.value">
            {{ language.label }}
          </option>
        </select>
      </cms-filter-group>

      <cms-filter-group :label="''">
        <button class="cms-btn cms-btn-secondary" type="button" :disabled="isLoading" @click="loadCategories">
          <v-icon icon="refresh" size="sm" />
          <span>{{ $t('general.refresh') }}</span>
        </button>
      </cms-filter-group>
    </cms-filter-bar>

    <div v-if="errorMessage" class="cms-help-center-error">
      <p>{{ errorMessage }}</p>
      <button type="button" class="cms-btn-link" @click="loadCategories">{{ $t('general.retry') }}</button>
    </div>

    <div v-if="isLoading" class="cms-help-center-loading">
      <div class="cms-loading-spinner" />
      <p>{{ $t('general.loading') }}</p>
    </div>

    <cms-empty-state
      v-else-if="categories.length === 0"
      :message="$t('cms.faq.list.emptyStateTitle')"
      :hint="$t('cms.faq.list.emptyStateHint')"
    />

    <cms-grid v-else min-width="360px">
      <cms-card
        v-for="category in categories"
        :key="category.id"
        clickable
        class="cms-category-card"
        @click="navigateToEditCategory(category.id)"
      >
        <template #actions>
          <cms-icon-button
            icon="pencil"
            variant="primary"
            :tooltip="$t('general.edit')"
            @click.stop="navigateToEditCategory(category.id)"
          />
          <cms-icon-button
            icon="trash"
            variant="danger"
            :disabled="deletingId === category.id"
            :tooltip="$t('general.delete')"
            @click.stop="deleteCategory(category)"
          />
          <cms-icon-button
            icon="plus"
            variant="secondary"
            :tooltip="$t('cms.faq.addFaqToCategory')"
            @click.stop="navigateToCreateFaq(category.id)"
          />
        </template>

        <div class="cms-category-header">
          <h3 class="cms-category-title">{{ category.name }}</h3>
          <span v-if="category.description" class="cms-category-description">{{ category.description }}</span>
        </div>

        <ul class="cms-category-meta">
          <li>
            <v-icon icon="help-circle" size="sm" />
            <span>{{ category.faqsCount || 0 }} {{ category.faqsCount === 1 ? $t('cms.faq.faq') : $t('cms.faq.faqs') }}</span>
          </li>
          <li v-if="category.is_public">
            <v-icon icon="eye" size="sm" />
            <span>{{ $t('general.published') }}</span>
          </li>
        </ul>

        <template #footer>
          <div class="cms-card-footer-actions">
            <cms-button variant="secondary" size="sm" @click.stop="navigateToManageFaqs(category.id)">
              {{ $t('cms.faq.manageFaqs') }}
            </cms-button>
          </div>
        </template>
      </cms-card>
    </cms-grid>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toastification';
import api from '@/api';
import { useAuthStore } from '@/stores/auth';
import {
  CmsPageHeader,
  CmsFilterBar,
  CmsFilterGroup,
  CmsGrid,
  CmsCard,
  CmsIconButton,
  CmsButton,
  CmsEmptyState,
} from '@/components/cms';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import { normalizeResource, extractRelationship } from '@/composables/useCmsActivityData';

interface CategoryCard {
  id: number;
  name: string;
  description?: string;
  faqsCount: number;
  is_public: boolean;
}

export default defineComponent({
  name: 'TheCmsFaq',
  components: {
    CmsPageHeader,
    CmsFilterBar,
    CmsFilterGroup,
    CmsGrid,
    CmsCard,
    CmsIconButton,
    CmsButton,
    CmsEmptyState,
    VIcon,
  },
  setup() {
    const router = useRouter();
    const { t } = useI18n();
    const toast = useToast();
    const authStore = useAuthStore();

    const agencySlug = computed(() => authStore.agency?.slug ?? null);
    const categories = ref<CategoryCard[]>([]);
    const isLoading = ref(false);
    const errorMessage = ref('');
    const deletingId = ref<number | null>(null);
    const selectedLanguage = ref('en');

    const languageOptions = [
      { value: 'en', label: 'English' },
      { value: 'es', label: 'Spanish' },
      { value: 'nl', label: 'Dutch' },
    ];

    const extractListData = (response: any): any[] => {
      if (!response) return [];
      const candidates = [
        response?.data?.data,
        response?.data,
        response?.data?.data?.data,
        response,
      ];
      for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
          return candidate;
        }
      }
      return [];
    };

    const loadCategories = async () => {
      if (!agencySlug.value) {
        return;
      }

      isLoading.value = true;
      errorMessage.value = '';

      try {
        const response = await api.faqCategoryService.listCmsFaqCategories(agencySlug.value, {
          perPage: 0, // Get all categories
          filters: {
            language: selectedLanguage.value,
          },
        });

        const list = extractListData(response);
        const locale = selectedLanguage.value;

        categories.value = list.map((category: any) => {
          const normalized = normalizeResource(category) || category;
          const translations = normalizeResource(extractRelationship(category, 'translations')) || [];
          const translation = translations.find((t: any) => t.locale === locale) || translations[0] || {};

          // Get FAQs count
          const faqs = normalizeResource(extractRelationship(category, 'faqs')) || [];
          const faqsCount = Array.isArray(faqs) ? faqs.length : 0;

          return {
            id: normalized.id,
            name: translation.name || '',
            description: translation.description || '',
            faqsCount,
            is_public: normalized.is_public || false,
          };
        });
      } catch (error: any) {
        console.error('Failed to load FAQ categories', error);
        errorMessage.value = error?.response?.data?.message || t('general.errorMessage');
        toast.error(errorMessage.value);
      } finally {
        isLoading.value = false;
      }
    };

    const navigateToCreateCategory = () => {
      router.push({ name: 'cms.help-center.create-category' });
    };

    const navigateToEditCategory = (categoryId: number) => {
      router.push({ name: 'cms.help-center.edit-category', params: { id: categoryId } });
    };

    const navigateToCreateFaq = (categoryId: number) => {
      router.push({ name: 'cms.help-center.create-faq', query: { category_id: categoryId } });
    };

    const navigateToManageFaqs = (categoryId: number) => {
      router.push({ name: 'cms.help-center.faqs', params: { categoryId } });
    };

    const deleteCategory = async (category: CategoryCard) => {
      if (!agencySlug.value) return;
      if (!window.confirm(t('general.deleteConfirmation') || 'Are you sure?')) {
        return;
      }

      deletingId.value = category.id;
      try {
        await api.faqCategoryService.deleteCmsFaqCategory(agencySlug.value, category.id);
        categories.value = categories.value.filter((item) => item.id !== category.id);
        toast.success(t('cms.faq.deleteSuccess'));
      } catch (error: any) {
        console.error('Failed to delete category', error);
        const message = error?.response?.data?.message || t('general.errorMessage');
        toast.error(message);
      } finally {
        deletingId.value = null;
      }
    };

    onMounted(() => {
      loadCategories();
    });

    return {
      categories,
      isLoading,
      errorMessage,
      deletingId,
      selectedLanguage,
      languageOptions,
      loadCategories,
      navigateToCreateCategory,
      navigateToEditCategory,
      navigateToCreateFaq,
      navigateToManageFaqs,
      deleteCategory,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/components/cms/cms-styles.scss';

.cms-help-center {
  color: $cms-text-primary;
}

.cms-help-center-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: $cms-spacing-xxl 0;
  gap: $cms-spacing-sm;
  color: $cms-text-secondary;
}

.cms-loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid $cms-border-color-light;
  border-top-color: $cms-primary-color;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.cms-help-center-error {
  padding: $cms-spacing-lg;
  background: $cms-danger-light;
  border: 1px solid $cms-danger-color;
  border-radius: $cms-radius-md;
  color: $cms-danger-solid;
  text-align: center;

  p {
    margin: 0 0 $cms-spacing-sm 0;
  }
}

.cms-category-card {
  .cms-category-header {
    margin-bottom: $cms-spacing-md;

    .cms-category-title {
      font-size: 1.25rem;
      font-weight: 600;
      margin: 0 0 $cms-spacing-xs 0;
      color: $cms-text-primary;
    }

    .cms-category-description {
      font-size: 0.875rem;
      color: $cms-text-secondary;
      display: block;
    }
  }

  .cms-category-meta {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    gap: $cms-spacing-md;
    flex-wrap: wrap;

    li {
      display: flex;
      align-items: center;
      gap: $cms-spacing-xs;
      font-size: 0.875rem;
      color: $cms-text-secondary;
    }
  }
}
</style>
